import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import type { ClockingLegendItem } from "./types";
import { getStatusColor } from "./status/displayStatusColors";

type StatusLegendProps = {
  items: ClockingLegendItem[];
  className?: string;
  dense?: boolean;
};

export function StatusLegend({ items, className, dense }: StatusLegendProps) {
  if (!items.length) return null;

  return (
    <div className={cn(dense ? "flex flex-col gap-2" : "flex flex-wrap items-center gap-1.5", className)}>
      {items.map((item) => {
        const color = getStatusColor(item.code);
        return (
          <div key={item.code} className="flex items-center gap-2">
            <span className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", color.accent)} />
            <span className="text-xs text-muted-foreground">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Compact chip when board clocking incentives are on. */
export function IncentivesEnabledChip({
  className,
  clockInPoints,
  clockOutPoints,
  missedClockPenalty,
  onClick,
}: {
  className?: string;
  clockInPoints?: number;
  clockOutPoints?: number;
  missedClockPenalty?: number;
  onClick?: () => void;
}) {
  const detailParts = [
    clockInPoints != null ? `In +${clockInPoints}` : null,
    clockOutPoints != null ? `Out +${clockOutPoints}` : null,
    missedClockPenalty != null ? `Miss −${missedClockPenalty}` : null,
  ].filter(Boolean);

  const label = detailParts.length
    ? `Incentives enabled · ${detailParts.join(" · ")}`
    : "Incentives enabled";

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label={onClick ? `${label} · Open settings` : label}
      onClick={onClick}
      className={cn(
        "h-8 gap-1.5 px-2.5 text-xs font-normal",
        !onClick && "pointer-events-none",
        className
      )}
    >
      <span className="size-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
      Incentives on
    </Button>
  );
}
